import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { SessionExpirationModal } from '../../components/common/SessionExpirationModal';
import { useAuth } from './AuthContext';

interface SessionExpirationContextType {
  showSessionExpired: (message?: string) => void;
}

const SessionExpirationContext = createContext<SessionExpirationContextType | undefined>(undefined);

// Global callback to show session expiration modal from anywhere (e.g., axios interceptor)
let globalShowSessionExpired: ((message?: string) => void) | null = null;
// Flag to track if modal is already shown - prevents message updates from subsequent 401 errors
let isModalShown = false;

// Reset the flag when modal is closed (for testing purposes)
export const resetSessionExpirationModal = () => {
  isModalShown = false;
};

export const setGlobalSessionExpirationHandler = (handler: (message?: string) => void) => {
  globalShowSessionExpired = handler;
};

export const showSessionExpiredGlobal = (message?: string) => {
  // If modal is already shown, don't update the message
  if (isModalShown) {
    return;
  }
  
  if (globalShowSessionExpired) {
    isModalShown = true;
    globalShowSessionExpired(message);
  }
};

export const SessionExpirationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string>('Your session has expired due to inactivity. Please login again to continue.');
  const { logout } = useAuth();

  const showSessionExpired = useCallback((customMessage?: string) => {
    if (customMessage) {
      setMessage(customMessage);
    } else {
      setMessage('Your session has expired due to inactivity. Please login again to continue.');
    }
    setIsOpen(true);
  }, []);

  // Set global handler on mount
  useEffect(() => {
    setGlobalSessionExpirationHandler(showSessionExpired);
    return () => {
      setGlobalSessionExpirationHandler(() => {});
    };
  }, [showSessionExpired]);

  const handleRedirect = useCallback(() => {
    setIsOpen(false);
    // Reset the global flag so modal can be shown again if needed
    resetSessionExpirationModal();
    logout();
    // Use window.location.href to ensure full page redirect
    window.location.href = '/login';
  }, [logout]);

  const handleClose = useCallback(() => {
    // Don't allow closing the modal - user must redirect to login
    handleRedirect();
  }, [handleRedirect]);

  return (
    <SessionExpirationContext.Provider value={{ showSessionExpired }}>
      {children}
      <SessionExpirationModal
        open={isOpen}
        onClose={handleClose}
        onRedirect={handleRedirect}
        message={message}
      />
    </SessionExpirationContext.Provider>
  );
};

export function useSessionExpiration() {
  const context = useContext(SessionExpirationContext);
  if (context === undefined) {
    throw new Error('useSessionExpiration must be used within a SessionExpirationProvider');
  }
  return context;
}

